import { useEffect, useRef, useState, type FormEvent } from "react";
import { api, type AssistantMessage } from "../lib/api";

/**
 * The chat bubble — Orbit's assistant, on every signed-in page.
 *
 * Jonathan, 2026-10-09: bring the Ask out of the Books tab as a floating bubble
 * "like Silverlock's". The server (`api/assistant.py`) does the work: Claude
 * picks a read-only, reach-scoped tool and the server runs the query, so this
 * component only carries the conversation and shows the reply.
 *
 * ⚠️ It hides itself when the assistant is not armed (`/api/assistant/status`),
 * so it is never a button that only errors — the same reason the Enter-a-bill
 * extract button checks before it offers itself.
 *
 * Self-contained inline styles rather than new classes in styles.css: this is
 * one floating element over the whole app, and keeping its look in one file is
 * what lets it be dropped in or pulled out without touching the global sheet.
 */

const NAVY = "#17233a";
const GREEN = "#1a7f5a";
const LINE = "#e7ece9";

// The empty-state starter questions — click one to send it. Moved here from the
// retired Books → Ask page, and broadened past its ledger-only examples because
// the bubble now answers balances, activity, payables and reports too, not just
// a QuickBooks figure.
const SAMPLES = [
  "How much cash do we have?",
  "How much was consultants in August?",
  "Any unusual payments lately?",
  "What's due this week?",
  "Show me the balance sheet",
];

type Turn = AssistantMessage | { role: "error"; content: string };

export default function Assistant() {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [open, setOpen] = useState(false);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  // Only ever shown when armed. A failure to reach the status endpoint hides it
  // too — an assistant we cannot confirm is on is one we do not offer.
  useEffect(() => {
    let live = true;
    api
      .assistantStatus()
      .then((r) => live && setEnabled(r.data.enabled))
      .catch(() => live && setEnabled(false));
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [turns, open]);

  if (!enabled) return null;

  // One path for both the input box and a sample-question chip, so a click
  // sends exactly what typing it would.
  async function ask(text: string) {
    const q = text.trim();
    if (!q || sending) return;
    const history: AssistantMessage[] = [
      ...turns.filter((t): t is AssistantMessage => t.role !== "error"),
      { role: "user", content: q },
    ];
    setTurns((prev) => [...prev, { role: "user", content: q }]);
    setDraft("");
    setSending(true);
    try {
      const { data } = await api.assistantChat(history);
      setTurns((prev) => [...prev, { role: "assistant", content: data.reply }]);
    } catch (err) {
      const detail =
        err && typeof err === "object" && "detail" in err
          ? String((err as { detail: unknown }).detail)
          : "Something went wrong. Try again.";
      setTurns((prev) => [...prev, { role: "error", content: detail }]);
    } finally {
      setSending(false);
    }
  }

  function send(event: FormEvent) {
    event.preventDefault();
    void ask(draft);
  }

  return (
    <div className="no-print">
      {open ? (
        <section
          aria-label="VGH Hub Assistant"
          style={{
            position: "fixed",
            // Clears the phone taskbar when it is present: --fab-bottom is set
            // in the narrow-screen block (styles.css) and falls back to the
            // desktop 24px otherwise.
            bottom: "calc(var(--fab-bottom, 24px) + 64px)",
            right: 24,
            zIndex: 1000,
            width: "min(380px, calc(100vw - 32px))",
            height: "min(560px, calc(100vh - 140px))",
            display: "flex",
            flexDirection: "column",
            background: "#fff",
            border: `1px solid ${LINE}`,
            borderRadius: 14,
            boxShadow: "0 18px 50px rgba(15,23,32,.22)",
            overflow: "hidden",
          }}
        >
          <header
            style={{
              background: NAVY,
              color: "#fff",
              padding: "12px 16px",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <strong style={{ fontSize: 14, flex: 1 }}>VGH Hub Assistant</strong>
            <button
              type="button"
              aria-label="Close"
              onClick={() => setOpen(false)}
              style={{
                background: "transparent",
                border: "none",
                color: "#fff",
                fontSize: 20,
                cursor: "pointer",
                lineHeight: 1,
              }}
            >
              ×
            </button>
          </header>

          <div
            ref={listRef}
            style={{ flex: 1, overflowY: "auto", padding: 14, display: "flex", flexDirection: "column", gap: 10 }}
          >
            {turns.length === 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div>
                  <p style={{ color: "#1f2a24", fontSize: 15, fontWeight: 600, margin: "2px 0 4px" }}>
                    Ask me anything
                  </p>
                  <p style={{ color: "#6b7a72", fontSize: 12.5, margin: 0, lineHeight: 1.45 }}>
                    Balances, transactions, what&apos;s due, payroll, a P&amp;L — I read it and
                    answer in plain words. I only see what you see, and I can&apos;t move money.
                  </p>
                </div>
                <div className="prompt-chips" style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {SAMPLES.map((q) => (
                    <button
                      key={q}
                      type="button"
                      className="prompt-chip"
                      disabled={sending}
                      onClick={() => void ask(q)}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
            {turns.map((t, i) => (
              <Bubble key={i} turn={t} />
            ))}
            {sending ? (
              <div style={{ color: "#6b7a72", fontSize: 13 }}>Thinking…</div>
            ) : null}
          </div>

          <form
            onSubmit={send}
            style={{ borderTop: `1px solid ${LINE}`, padding: 10, display: "flex", gap: 8 }}
          >
            <input
              aria-label="Ask the assistant"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="How much cash do we have?"
              maxLength={4000}
              autoFocus
              style={{
                flex: 1,
                border: `1px solid ${LINE}`,
                borderRadius: 9,
                padding: "9px 11px",
                fontSize: 14,
                outline: "none",
              }}
            />
            <button
              type="submit"
              disabled={sending || !draft.trim()}
              style={{
                background: GREEN,
                color: "#fff",
                border: "none",
                borderRadius: 9,
                padding: "0 14px",
                fontWeight: 600,
                cursor: sending || !draft.trim() ? "default" : "pointer",
                opacity: sending || !draft.trim() ? 0.5 : 1,
              }}
            >
              Send
            </button>
          </form>
        </section>
      ) : null}

      <button
        type="button"
        aria-label={open ? "Hide assistant" : "Ask the assistant"}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        style={{
          position: "fixed",
          // Lifted above the phone taskbar when one is on screen; --fab-bottom
          // is defined in the narrow-screen block, 24px on desktop.
          bottom: "var(--fab-bottom, 24px)",
          right: 24,
          zIndex: 1001,
          width: 52,
          height: 52,
          borderRadius: "50%",
          background: NAVY,
          color: "#fff",
          border: "none",
          boxShadow: "0 10px 26px rgba(15,23,32,.3)",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 11.5a8.5 8.5 0 0 1-12.4 7.5L4 20l1.1-4.2A8.5 8.5 0 1 1 21 11.5z" />
        </svg>
      </button>
    </div>
  );
}

/** One line in the thread. The model's reply can carry newlines; they are
 * preserved. Errors read in the alarm colour so a failed turn is not mistaken
 * for an answer. */
function Bubble({ turn }: { turn: Turn }) {
  if (turn.role === "error") {
    return (
      <div style={{ alignSelf: "flex-start", maxWidth: "90%", color: "var(--alarm, #b4232a)", fontSize: 13 }}>
        {turn.content}
      </div>
    );
  }
  const mine = turn.role === "user";
  return (
    <div
      style={{
        alignSelf: mine ? "flex-end" : "flex-start",
        maxWidth: "90%",
        background: mine ? "#eef4f1" : "#f6f8f7",
        color: "#1f2a24",
        border: `1px solid ${LINE}`,
        borderRadius: 11,
        padding: "8px 11px",
        fontSize: 14,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      }}
    >
      {turn.content}
    </div>
  );
}
